import { useSidebar } from '../../contexts/SidebarContext';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { useDemo } from '../../contexts/DemoContext';
import { useUserData } from '../../hooks/useUserData';
import Icon from '../AppIcon';
import Button from './Button';
import ScenarioSelector from '../demo/ScenarioSelector';

const Header = ({ showTabBar = false }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOutUser } = useAuth();
  const { isPaid } = useSubscription();
  const { sidebarExpanded, toggleSidebar } = useSidebar();
  const { isDemoModeActive, toggleDemoMode } = useDemo();
  const { userData } = useUserData();
  const [avatarFailed, setAvatarFailed] = useState(false);

  useEffect(() => {
    setAvatarFailed(false);
  }, [user?.photoURL]);

  const renderAvatar = () => {
    if (!user) {
      return (
        <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
          <Icon name="User" size={18} className="text-white" />
        </div>
      );
    }

    const displayName = user.displayName || user.email || 'User';
    let initialLetters = '';
    
    try {
      initialLetters = displayName
        .trim()
        .split(/\s+/)
        .filter(Boolean)
        .map((part) => part[0]?.toUpperCase())
        .filter(Boolean)
        .join('')
        .slice(0, 2);
    } catch (e) {
      initialLetters = '';
    }

    // Try to show photo first (only if URL exists and hasn't failed)
    if (user.photoURL && user.photoURL.trim() && !avatarFailed) {
      return (
        <img
          src={user.photoURL}
          alt={displayName}
          className="w-10 h-10 rounded-full object-cover flex-shrink-0 border-2 border-gray-200"
          onError={() => setAvatarFailed(true)}
        />
      );
    }

    // Fallback to initials if available and valid
    if (initialLetters && initialLetters.length > 0) {
      return (
        <div className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center text-sm font-semibold text-white flex-shrink-0 border-2 border-blue-700">
          {initialLetters}
        </div>
      );
    }

    // Always fallback to default User icon
    return (
      <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center flex-shrink-0">
        <Icon name="User" size={18} className="text-white" />
      </div>
    );
  };

  const getPageTitle = () => {
    const pathMap = {
      '/wealth-dashboard': 'Wealth Dashboard',
      '/learning-center': 'Learning Center',
      '/income-management': 'Income Management',
      '/expense-tracking': 'Expense Tracking',
      '/asset-portfolio': 'Asset Portfolio',
      '/debt-liabilities': 'Debt & Liabilities',
      '/investments-allocations': 'Investments & Allocations',
      '/scenario-planning': 'Scenario Planning',
      '/monte-carlo': 'Monte Carlo Simulation',
      '/reports-analytics': 'Reports & Analytics',
      '/simple': 'Quick Insight Mode',
      '/simplified-version': 'Quick Insight Mode'
    };
    return pathMap[location.pathname] || 'RetireProApp';
  };

  const getPageDescription = () => {
    const descriptionMap = {
      '/wealth-dashboard': 'Monitor your retirement readiness and key performance indicators',
      '/learning-center': 'Understand the philosophy, principles, and tools behind financial confidence',
      '/income-management': 'Manage salary projections and income streams',
      '/expense-tracking': 'Track spending patterns and budget allocations',
      '/asset-portfolio': 'Review investment portfolio and asset allocation',
      '/debt-liabilities': 'Track loans, liabilities, and optimize debt paydown strategy',
      '/investments-allocations': 'Track recurring investments and their allocation across asset classes',
      '/scenario-planning': 'Model different retirement scenarios and stress tests',
      '/monte-carlo': 'Simulate wealth outcomes with probabilistic market and inflation assumptions',
      '/reports-analytics': 'Generate comprehensive reports and analytics',
      '/simple': 'Get instant feedback with just a few simple inputs',
      '/simplified-version': 'Get instant feedback with just a few simple inputs'
    };
    return descriptionMap[location.pathname] || 'Comprehensive retirement planning platform';
  };

  // Check if we're in quick mode
  const isQuickMode = location.pathname.startsWith('/simple') || location.pathname.startsWith('/simplified-version');

  // Calculate left position: tab bar (3rem/48px) + sidebar width
  // Tab bar is w-12 (3rem = 48px), sidebar is w-64 (16rem) expanded or w-16 (4rem) collapsed
  // When tab bar is shown, sidebar is positioned at lg:left-12 (3rem from left)
  // Header should start at: tab bar width (3rem) + sidebar width
  const getLeftPosition = () => {
    if (showTabBar) {
      // Tab bar is 3rem (48px), sidebar starts at 3rem, add sidebar width
      // Expanded: 3rem + 16rem = 19rem
      // Collapsed: 3rem + 4rem = 7rem
      return sidebarExpanded ? 'lg:left-[19rem]' : 'lg:left-[7rem]';
    } else {
      // No tab bar, sidebar starts at 0, just sidebar width
      // Expanded: 16rem, Collapsed: 4rem
      return sidebarExpanded ? 'lg:left-[16rem]' : 'lg:left-[4rem]';
    }
  };

  return (
    <header 
      className={`
        fixed top-0 right-0 h-16 bg-card border-b border-border z-1000
        transition-all duration-300 ease-spring
        left-0
        ${getLeftPosition()}
      `}
    >
      <div className="flex items-center justify-between h-full px-6">
        {/* Left Section - Page Info */}
        <div className="flex items-center gap-4">
          {/* Mobile Menu Toggle */}
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleSidebar}
            className="lg:hidden"
            aria-label="Toggle sidebar"
          >
            <Icon name="Menu" size={20} />
          </Button>

          <div className="flex flex-col">
            <h1 className="text-lg font-semibold text-foreground">
              {getPageTitle()}
            </h1>
            <p className="text-sm text-muted-foreground hidden sm:block">
              {getPageDescription()}
            </p>
          </div>
        </div>

        {/* Right Section - Actions */}
        <div className="flex items-center gap-3">
          {/* Notifications, Quick Actions, and Settings - temporarily disabled
          <Button
            variant="ghost"
            size="icon"
            className="relative"
            aria-label="Notifications"
          >
            <Icon name="Bell" size={20} />
            <span className="absolute -top-1 -right-1 w-2 h-2 bg-accent rounded-full"></span>
          </Button>

          <Button
            variant="ghost"
            size="icon"
            aria-label="Quick actions"
          >
            <Icon name="Plus" size={20} />
          </Button>

          <Button
            variant="ghost"
            size="icon"
            aria-label="Settings"
          >
            <Icon name="Settings" size={20} />
          </Button>
          */}

          {/* Demo Mode Toggle and Scenario Selector - Hide in Quick Mode */}
          {!isQuickMode && (
            <div className="flex items-center gap-3">
              {isDemoModeActive && <ScenarioSelector />}
              <Button
                variant={isDemoModeActive ? "default" : "outline"}
                size="sm"
                onClick={toggleDemoMode}
                className={`text-sm font-medium min-w-[140px] ${
                  !isDemoModeActive 
                    ? 'bg-background border-2 border-border shadow-sm hover:bg-muted hover:border-primary/50 active:bg-muted/80' 
                    : ''
                }`}
              >
                {isDemoModeActive ? 'Demo Mode ON' : 'Demo View - OFF'}
              </Button>
            </div>
          )}

          {/* User Profile */}
          <div className="flex items-center gap-3 pl-3 border-l border-border">
            {user ? (
              <>
                <div className="hidden sm:flex flex-col items-end">
                  <span className="text-sm font-medium text-foreground">
                    {user.displayName || user.email}
                  </span>
                </div>
                <div className="relative group">
                  <button
                    className="flex items-center gap-2 hover:opacity-80 transition-opacity focus:outline-none"
                    aria-label="User menu"
                    type="button"
                    aria-expanded="false"
                  >
                    {renderAvatar()}
                  </button>
                  {/* Dropdown Menu */}
                  <div className="absolute right-0 top-full mt-2 w-48 bg-card border border-border rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-[100]">
                    <div className="py-1">
                      {isPaid && (
                        <Link
                          to="/billing"
                          className="w-full px-4 py-2 text-left text-sm text-foreground hover:bg-muted transition-colors flex items-center gap-2"
                        >
                          <Icon name="CreditCard" size={16} />
                          Billing
                        </Link>
                      )}
                      <button
                        onClick={async () => {
                          try {
                            await signOutUser();
                            navigate('/');
                          } catch (error) {
                            console.error('Sign out error:', error);
                          }
                        }}
                        className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 transition-colors flex items-center gap-2"
                      >
                        <Icon name="LogOut" size={16} />
                        Sign Out
                      </button>
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => navigate('/login')}
                className="text-blue-600 hover:text-blue-700"
              >
                Sign In
              </Button>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;